import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import LockResetIcon from '@mui/icons-material/LockReset';
import { Alert, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { AppModal } from '../../../../components/ui/AppModal';
import { ApiError } from '../../../../lib/http/ApiError';
import { useNotification } from '../../../notifications/hooks/useNotification';
import { useSendPasswordLink } from '../../hooks/useSendPasswordLink';
import type { User } from '../../userTypes';
import { isAwaitingActivation } from '../../utils/awaitingActivation';

interface ResetPasswordDialogProps {
  /** The target user; `null` keeps the dialog closed. */
  user: User | null;
  onClose: () => void;
  /** Called after a successful request — or when the request proved the displayed
   * state stale (403/404) — so the caller can re-read the user. */
  onSuccess: () => void;
}

/**
 * Password-link confirm dialog, shared by the list row action (HOMCO-1366) and the
 * fiche's block C (HOMCO-1364). Calls POST /users/:id/password-link (HOMCO-1393):
 * the current password stops working at once, every session is revoked and a
 * single-use link is e-mailed — nobody, not even the operator, sees a password.
 * Admin-only server-side; callers only offer it to an `admin`, never on one's own
 * account, and only for an active one.
 *
 * `email_sent: false` means the link was issued but the mail did not leave: the
 * account is still reset, so this is a warning, not an error.
 */
export function ResetPasswordDialog({ user, onClose, onSuccess }: ResetPasswordDialogProps) {
  const { t } = useTranslation('users');
  const { notify, notifySuccess } = useNotification();
  const { sendPasswordLink, loading: submitting, error } = useSendPasswordLink();

  const keys = user && isAwaitingActivation(user) ? 'actions.activation' : 'actions.reset';

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!user) return;
    const result = await sendPasswordLink(user.id);
    // null = failed: 5xx were toasted by the hook, 4xx are shown inline below.
    if (!result) return;
    if (result.email_sent) {
      notifySuccess(
        t(`${keys}.success`, {
          name: user.display_name || user.username,
          email: user.email,
        }),
      );
    } else {
      notify({ message: t('actions.reset.emailNotSent'), severity: 'warning' });
    }
    onSuccess();
    onClose();
  };

  // 403: target inactive, own account or caller not admin (state changed since the
  // page loaded); 404: account gone. Raw backend text stays out of the French UI.
  // 5xx and transport errors are toasted by the hook only — never shown twice.
  let errorMessage: string | null = null;
  const stale =
    ApiError.isApiError(error) && (error.statusCode === 403 || error.statusCode === 404);
  if (ApiError.isApiError(error) && error.isClientError) {
    if (error.statusCode === 403) errorMessage = t('actions.reset.forbidden');
    else if (error.statusCode === 404) errorMessage = t('actions.reset.notFound');
    else errorMessage = t('actions.reset.error');
  }

  // A 403/404 means the screen behind is out of date (account deactivated or gone):
  // retrying cannot succeed, so the confirm button is disabled and the caller re-reads
  // the user once.
  const staleNotifiedRef = useRef(false);
  useEffect(() => {
    if (stale && !staleNotifiedRef.current) {
      staleNotifiedRef.current = true;
      onSuccess();
    }
  }, [stale, onSuccess]);

  return (
    <AppModal
      open={user !== null}
      onClose={handleClose}
      disableClose={submitting}
      maxWidth="xs"
      titleId="reset-password-title"
      icon={<LockResetIcon sx={th => ({ fontSize: th.iconSize.sm })} />}
      title={t(`${keys}.confirmTitle`, { username: user?.username ?? '' })}
      closeLabel={t('actions.reset.cancel')}
    >
      <DialogContent sx={{ pt: 2 }}>
        <Typography>
          <Trans
            i18nKey={`${keys}.confirmMessage`}
            ns="users"
            values={{ email: user?.email ?? '' }}
            components={{ strong: <strong /> }}
          />
        </Typography>
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>
          {t('actions.reset.cancel')}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting || stale}>
          {t(`${keys}.confirm`)}
        </Button>
      </DialogActions>
    </AppModal>
  );
}
